import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { canAccess } from '../../constants/tiers';
import { toast } from 'sonner';
import { Copy, Mail, ChevronLeft, Lock, Loader2, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Peer Circles → Invite Friends (iter 73 Phase 1).
 *
 * Each member gets a unique link `${APP_URL}/start?ref={user_id}` returned
 * by GET /api/referrals/me. We render copy + mailto share, plus the live
 * referral count (incremented on each successful free-trial signup that
 * carried the `ref` param).
 */
const PortalPeerCirclesInvite = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const unlocked = canAccess(user?.role, 'peerCircles');

  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState('');
  const [count, setCount] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!unlocked) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API_URL}/api/referrals/me`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error('Could not load your invite link');
        const data = await res.json();
        setLink(data.referral_link || '');
        setCount(Number(data.referral_count || 0));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [unlocked]);

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy - please copy manually.');
    }
  };

  const emailShare = () => {
    if (!link) return;
    const subject = encodeURIComponent('Join me on Mundalo - we should plan our move together');
    const body = encodeURIComponent(
      `Hey,\n\nI've been using Mundalo to plan my move to Mexico - it's where I'm building my Blueprint, finding housing, and meeting other movers.\n\nUse my invite link to start your free 15-day trial:\n${link}\n\nSee you on the other side.`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  if (!unlocked) {
    return (
      <PortalLayout activeTab="peerCircles">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-14" data-testid="invite-locked">
          <div className="bg-white border border-[#EFE6CF] rounded-2xl p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#F4ECD8] flex items-center justify-center mb-4">
              <Lock className="w-6 h-6 text-[#1A5C6E]" />
            </div>
            <h1 className="font-display italic text-3xl text-[#0A1F2E] mb-2">Invites are part of Peer Circles</h1>
            <p className="text-[#0A1F2E]/70 mb-6">Unlock invites, the member directory, and DMs with a Community or Connected plan.</p>
            <Link
              to="/portal/upgrade"
              className="inline-flex items-center gap-1 bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-6 py-3 rounded-full transition"
              data-testid="invite-upgrade-cta"
            >
              See plans
            </Link>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout activeTab="peerCircles">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 md:py-14" data-testid="invite-page">
        <button
          type="button"
          onClick={() => navigate('/portal/peer-circles')}
          className="inline-flex items-center gap-1 text-[#1A5C6E] hover:text-[#0A1F2E] text-sm mb-6 transition font-mono uppercase tracking-[0.12em] text-[11px]"
          data-testid="invite-back-to-hub"
        >
          <ChevronLeft className="w-4 h-4" /> Back to Peer Circles
        </button>

        <header className="mb-8">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
            Peer Circles · Invite
          </p>
          <h1 className="font-display italic text-4xl md:text-5xl text-[#0A1F2E] leading-[1.05] mb-3">
            Bring a friend.
          </h1>
          <p className="font-body text-base md:text-lg text-[#0A1F2E]/75">
            Share your invite link. We&rsquo;ll let you know when someone joins through it.
          </p>
        </header>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div className="bg-[#B91C1C]/10 border border-[#B91C1C]/30 text-[#B91C1C] p-4 mb-6 rounded-xl text-sm" data-testid="invite-error">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Link card */}
            <section className="bg-white border border-[#EFE6CF] rounded-2xl p-5 sm:p-6 mb-5" data-testid="invite-link-card">
              <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-2">
                Your invite link
              </p>
              <div className="flex flex-col sm:flex-row gap-3 items-stretch">
                <input
                  type="text"
                  readOnly
                  value={link}
                  onFocus={(e) => e.target.select()}
                  className="flex-1 bg-[#F9F6EF] border border-[#EFE6CF] text-[#0A1F2E] rounded-lg px-3 py-2.5 text-sm font-mono"
                  data-testid="invite-link-input"
                />
                <button
                  type="button"
                  onClick={copyLink}
                  className="inline-flex items-center justify-center gap-2 bg-[#0A1F2E] hover:bg-[#163448] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-5 py-2.5 rounded-full transition"
                  data-testid="invite-copy-btn"
                >
                  <Copy className="w-4 h-4" /> Copy link
                </button>
                <button
                  type="button"
                  onClick={emailShare}
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-[#F9F6EF] border border-[#0A1F2E]/20 text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.15em] px-5 py-2.5 rounded-full transition"
                  data-testid="invite-email-btn"
                >
                  <Mail className="w-4 h-4" /> Share via email
                </button>
              </div>
            </section>

            {/* Stats card */}
            <section className="bg-white border border-[#EFE6CF] rounded-2xl p-5 sm:p-6 flex items-center gap-4" data-testid="invite-stats-card">
              <div className="w-12 h-12 rounded-full bg-[#F4ECD8] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-[#1A5C6E]" />
              </div>
              <div>
                <p className="font-display italic text-3xl text-[#0A1F2E] leading-none" data-testid="invite-count-value">
                  {count}
                </p>
                <p className="text-[#0A1F2E]/65 text-sm mt-1">
                  {count === 1 ? 'person has joined' : 'people have joined'} through your link.
                </p>
              </div>
            </section>
          </>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalPeerCirclesInvite;
